import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Inline status selector: shows a colored badge, click opens dropdown, persists via PUT /{collection}/{id}.
 * The dropdown panel is rendered in a portal anchored on document.body so it never gets clipped by
 * a parent with `overflow: hidden` (e.g. the rounded table card). When there's not enough room
 * below the trigger, it auto-flips above.
 * props:
 *  - value: current status string
 *  - options: string[] | { value, label?, className? }[]
 *  - styles: { [status]: tailwind classes } for badge color
 *  - onChange: async (newStatus) => void   (must throw to rollback on error)
 *  - testId?: string
 *  - disabled?: boolean
 */
export default function InlineStatus({ value, options, styles = {}, onChange, testId, disabled }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(value);
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, placement: 'bottom', minWidth: 140 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => { setCurrent(value); }, [value]);

  // Compute panel position relative to viewport, flipping above if not enough space below.
  const reposition = () => {
    const btn = triggerRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const panelH = panelRef.current?.offsetHeight || (options.length * 32 + 16);
    const spaceBelow = window.innerHeight - r.bottom;
    const placement = spaceBelow < panelH + 8 && r.top > panelH + 8 ? 'top' : 'bottom';
    const top = placement === 'bottom' ? r.bottom + 4 : r.top - panelH - 4;
    setPos({ top, left: r.left, placement, minWidth: Math.max(140, r.width) });
  };

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScroll = () => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const normalized = options.map((o) => typeof o === 'string' ? { value: o } : o);

  const select = async (e, v) => {
    e.stopPropagation();
    setOpen(false);
    if (v === current || saving) return;
    const prev = current;
    setCurrent(v);
    setSaving(true);
    try {
      await onChange?.(v);
    } catch {
      setCurrent(prev);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        disabled={disabled || saving}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-md transition-opacity ${styles[current] || 'bg-[#F3F4F8] text-[#4A4B63]'} ${disabled || saving ? 'opacity-60 cursor-wait' : 'hover:opacity-80 cursor-pointer'}`}
      >
        <span>{current || '—'}</span>
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>
      {open && createPortal((
        <div
          ref={panelRef}
          className="fixed z-[200] bg-white rounded-xl border border-[#EFF0F6] shadow-lg p-1"
          style={{ top: pos.top, left: pos.left, minWidth: pos.minWidth, boxShadow: '0 6px 24px rgba(26,27,46,0.12)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {normalized.map((o) => (
            <button
              key={o.value}
              type="button"
              data-testid={`${testId}-option-${o.value}`}
              onClick={(e) => select(e, o.value)}
              className={`w-full text-left text-[12.5px] font-medium px-3 py-1.5 rounded-lg flex items-center justify-between hover:bg-[#EEF2FF] ${current === o.value ? 'text-[#4F46E5]' : 'text-[#4A4B63]'}`}
            >
              <span className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${(styles[o.value] || 'bg-[#D4D5E0]').split(' ')[0]}`} style={{ opacity: 0.8 }} />
                {o.label || o.value}
              </span>
              {current === o.value && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      ), document.body)}
    </>
  );
}
